import { useState } from 'react';

import { Modal, Button } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { useAxios } from 'configs/requests';
import { Loading, Error } from 'components/Fetch';
import { useAppSelector } from 'hooks';
import { getAuthorizedSubgroups } from 'store/groups/reducer';
import { TABLE_EMPTY_VALUE } from 'constants/constants';

interface IRestoreDeviceModalProps {
  device: IDeviceListItem;
  onClose: () => void;
  onSuccess: () => void;
}

const RestoreDeviceModal = ({ device, onClose, onSuccess }: IRestoreDeviceModalProps) => {
  const intl = useIntl();
  const [err, setErr] = useState<any>(null);
  const allSubgroups = useAppSelector(getAuthorizedSubgroups);

  const groupName = allSubgroups.find((g) => g.gid === device.gid)?.name;

  const [{ loading, error }, patchRestore] = useAxios<void>(
    {
      url: `/clinician-gateway/device/${device.deviceId}/restore`,
      method: 'PATCH'
    },
    { manual: true }
  );

  const handleRestore = async () => {
    try {
      await patchRestore();
      onSuccess();
    } catch (e) {
      setErr(e);
    }
  };

  return (
    <Modal show onHide={onClose} data-test="restore-modal">
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage({ id: 'restoreDeviceKey' })}</Modal.Title>
      </Modal.Header>
      {(error || err) && <Error className="m-5" />}
      {loading ? (
        <Loading className="m-5" />
      ) : (
        <>
          <Modal.Body>
            <dl className="row mb-0 mt-3">
              <dt className="col-sm-3">{intl.formatMessage({ id: 'typeKey' })}</dt>
              <dd className="col-sm-9">
                {device.deviceOrdering?.displayType
                  ? intl.formatMessage({ id: device.deviceOrdering.displayType })
                  : TABLE_EMPTY_VALUE}
              </dd>

              <dt className="col-sm-3">{intl.formatMessage({ id: 'deviceKey' })}</dt>
              <dd className="col-sm-9">
                {device.deviceOrdering?.displayName
                  ? intl.formatMessage({ id: device.deviceOrdering.displayName })
                  : TABLE_EMPTY_VALUE}
              </dd>

              <dt className="col-sm-3">{intl.formatMessage({ id: 'alternateIdKey' })}</dt>
              <dd className="col-sm-9">{device.patient?.alternateId || TABLE_EMPTY_VALUE}</dd>

              <dt className="col-sm-3">{intl.formatMessage({ id: 'imeiKey' })}</dt>
              <dd className="col-sm-9">{device.imei || TABLE_EMPTY_VALUE}</dd>

              <dt className="col-sm-3">{intl.formatMessage({ id: 'groupsKey' })}</dt>
              <dd className="col-sm-9">{groupName || TABLE_EMPTY_VALUE}</dd>
            </dl>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onClose} data-test="restore-cancel">
              {intl.formatMessage({ id: 'Cancel' })}
            </Button>
            <Button variant="primary" onClick={handleRestore} data-test="restore-confirm">
              {intl.formatMessage({ id: 'restoreDeviceKey' })}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export { RestoreDeviceModal };
